import { useState, useEffect } from 'react';
import { Sword, Shield, Heart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { profileStore } from '@/services/ProfileStore';
import { CHARACTER_OPTIONS } from '@/config/tileIndex';
import { cn } from '@/lib/utils';

interface CharacterSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CharacterSelectionModal({ open, onOpenChange }: CharacterSelectionModalProps) {
  const [selectedCharacter, setSelectedCharacter] = useState<string>('kim');

  // Load selected character on mount
  useEffect(() => {
    const settings = profileStore.getSettings();
    setSelectedCharacter(settings.selectedCharacter);
  }, [open]);

  const handleCharacterSelect = (characterId: string) => {
    setSelectedCharacter(characterId);
    profileStore.updateSettings({ selectedCharacter: characterId });
    onOpenChange(false); // Close modal after selection
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-game-panel/95 border-2 border-game-panel-border max-w-3xl backdrop-blur-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary flex items-center gap-2">
            <span className="text-2xl">⚔️</span>
            Choose Your Character
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Select your adventurer class. Each has unique stats and abilities.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
          {CHARACTER_OPTIONS.map((character) => (
            <div
              key={character.id}
              className={cn(
                "p-5 rounded-xl border-2 cursor-pointer transition-all duration-200",
                selectedCharacter === character.id
                  ? "border-primary bg-primary/15 shadow-lg shadow-primary/20 scale-[1.02]"
                  : "border-border/60 bg-secondary/30 hover:border-accent/60 hover:bg-secondary/50 hover:scale-[1.01]"
              )}
              onClick={() => handleCharacterSelect(character.id)}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={cn(
                  "w-14 h-14 rounded-xl flex items-center justify-center transition-all border-2",
                  selectedCharacter === character.id 
                    ? "bg-primary/20 border-primary/40" 
                    : "bg-secondary border-border/40"
                )}>
                  <Sword className={cn(
                    "w-7 h-7 transition-colors",
                    selectedCharacter === character.id 
                      ? "text-primary" 
                      : "text-muted-foreground"
                  )} />
                </div>
                <div className="flex-1">
                  <h3 className={cn(
                    "font-bold text-lg mb-1",
                    selectedCharacter === character.id ? "text-primary" : "text-foreground"
                  )}>
                    {character.name}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-snug">
                    {character.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                  <Heart className="w-5 h-5 text-red-400" />
                  <span className="font-bold text-red-400">{character.stats.hp}</span>
                  <span className="text-xs text-muted-foreground">HP</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20">
                  <Sword className="w-5 h-5 text-orange-400" />
                  <span className="font-bold text-orange-400">{character.stats.attack}</span>
                  <span className="text-xs text-muted-foreground">ATK</span>
                </div>
                <div className="flex flex-col items-center gap-1.5 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <span className="font-bold text-blue-400">{character.stats.defense}</span>
                  <span className="text-xs text-muted-foreground">DEF</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t-2 border-border/40">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="hover:scale-105 transition-transform"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleCharacterSelect(selectedCharacter)}
            className="hover:scale-105 transition-transform"
          >
            Confirm Selection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}